/**
 * Step 2 — "What it means for them" (rebuild M3, 19 Oct 2026; the agreed mock-up's j1-03).
 *
 * The four numbers with this support and nothing else changed — the comparison every plan on step 3
 * is read against, priced by the engine's own baseline (useSahBaseline) so they are the strip there.
 * Then their contribution for each hour, which means test sets the Age Pension and what that means for
 * the ways to help, how long the money lasts (and each person's lifetime limit), and what the rates
 * would be if Centrelink assessed them as an illness-separated couple.
 */
import React, { useMemo, useState } from 'react';
import DoNothingStrip from '../agedCare/DoNothingStrip.tsx';
import DialogShell from '../../components/ui/DialogShell.tsx';
import { Button } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { calculateSahOptimizationTargets, resolveIllnessSeparatedForSahInsight } from './supportAtHomePensionInsightUtils.tsx';
import { anyoneOwnsHome } from '../../../../shared/src/calculation/householdPosition.ts';
import { getAgedCareModellerResidencyOverrides } from '../../../../shared/src/calculation/householdStatus.ts';
import { LIFETIME_CAPS } from '../../../../shared/src/calculation/supportAtHomeCalculations.ts';
import { ANNUAL_REDUCTION_PER_1000_ASSETS } from '../../constants/index.ts';
import { SahFigurePopover, type SahFigures, type SahFigurePage } from './SahPlanDialogs.tsx';
import { useSahBaseline, type SahBaselineFigures } from './useSahBaseline.ts';
import { Eye } from 'lucide-react';
import { ProjectionYearsInput } from '../../components/ui/ProjectionYearsInput.tsx';
import { FundingRunway } from '../../components/narrative/FundingRunway.tsx';
import { buildMoneySequence, HorizonNote, type RunwayRow } from '../agedCare/moneySequence.tsx';
import SupportAtHomeSuggestionEngineDeepDive, { type SupportAtHomeSuggestionEngineDeepDiveProps } from './SupportAtHomeSuggestionEngineDeepDive.tsx';
import { sahMoveAnswer, sahEnteringPerson } from './sahEngineContext.ts';
import { SahLinkedCareSummary, linkedCareStory, depositRefundSentence } from './SahLinkedCareSummary.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { SavedStrategy } from '../../strategy/StrategyIntegrationUtils.ts';
import { SAH_BENCH } from '../../utils/benchmarkWords.ts';

export interface SahStep2Props {
    factFind: DraftFactFind;
    servicePlan: { client?: Record<string, unknown>; partner?: Record<string, unknown> };
    years: number;
    supportStartDate?: string;
    savedStrategies: ReadonlyArray<SavedStrategy>;
    onBack: () => void;
    onNext: () => void;
    /** The official letter's rates (the contributions screen's override, kept for compliance). */
    onEnterLetterRates: () => void;
    /** Change the years planned for, here (owner, 20 Oct 2026, as aged care's step 2). */
    onYearsChange?: (years: number) => void;
}

const pct = (r: number) => `${+(r * 100).toFixed(1)}%`;
const money = (n: number) => formatCurrency(Math.abs(Math.round(n)));

/** The baseline in the shape the figure pop-over reads. */
export function figuresFromBaseline(b: SahBaselineFigures, livingExpenses: number): SahFigures {
    const y1 = (b.projections[0] || {}) as Record<string, unknown>;
    return {
        contribution: b.contribution,
        pension: b.pension,
        cashflow: b.cashflow,
        legacy: b.legacy,
        investmentIncome: Number(y1.investmentIncome) || Number(b.financials.investmentIncome) || 0,
        rates: b.rates,
        breakdown: {
            clinical: b.costs.clinical, independence: b.costs.independence, everydayLiving: b.costs.everyday,
            independenceContribution: b.costs.contribution.independence, everydayContribution: b.costs.contribution.everyday, privateExcess: b.costs.privateExcess,
        },
        livingExpenses,
        taxPayable: Number(b.financials.netTax) || 0,
        pensionRows: (b.financials.socialSecurityBreakdown as Array<{ label?: string; value?: unknown }>) || [],
        cashFlowBreakdown: y1.cashFlowBreakdown || b.financials.cashFlowBreakdown,
    };
}

const Why = ({ onClick, children = 'why?' }: { onClick: () => void; children?: React.ReactNode }) => (
    <button type="button" onClick={onClick} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">{children}</button>
);

const Section = ({ title, children, testId }: { title: string; children: React.ReactNode; testId?: string }) => (
    <section className="border-t border-slate-100 pt-5 first:border-t-0 first:pt-0" data-testid={testId}>
        <h3 className="text-[15px] font-bold text-brand-navy">{title}</h3>
        <div className="mt-2">{children}</div>
    </section>
);

const SahStep2 = ({ factFind, servicePlan, years, supportStartDate, savedStrategies, onBack, onNext, onEnterLetterRates, onYearsChange }: SahStep2Props) => {
    const v = householdVoice(factFind);
    const isCouple = factFind?.adviceFor === 'couple';
    const { figures: b, loading, error, linkedCare } = useSahBaseline({ factFind, servicePlan, years, supportStartDate, savedStrategies });
    // With a partner moving into care, the household priced is the aged care plan carried out: the runway,
    // the living costs and the year-by-year read that household, not the profile as entered (24 Oct 2026).
    const household = linkedCare?.after ?? factFind;
    const linkedStory = useMemo(() => (linkedCare ? linkedCareStory(linkedCare) : null), [linkedCare]);
    // The illness-separated rates, where a move into care would make them so and it is not already priced that way.
    const entering = sahEnteringPerson(factFind);
    const separatedOverrides = useMemo(() => getAgedCareModellerResidencyOverrides(factFind as unknown as FactFind) as Record<string, unknown>, [factFind]);
    const showSeparated = isCouple && !!entering && sahMoveAnswer(factFind) !== 'yes' && Object.keys(separatedOverrides).length > 0;
    const separated = useSahBaseline({ factFind, servicePlan, years, supportStartDate, savedStrategies, extraStrategyOptions: separatedOverrides, enabled: showSeparated });
    const [page, setPage] = useState<SahFigurePage | null>(null);
    const [yearsOpen, setYearsOpen] = useState(false);
    const [separatedOpen, setSeparatedOpen] = useState(false);
    const living = Number((household?.expenses as { livingExpenses?: unknown } | undefined)?.livingExpenses) || 0;
    const fig = b ? figuresFromBaseline(b, living) : null;
    const servicesTotal = b ? b.costs.totalAnnual : 0;

    const pensionStory = (() => {
        if (!b) return null;
        const data = calculateSahOptimizationTargets({
            assessableAssets: b.assessableAssets,
            assessableIncome: b.assessableIncome,
            currentPension: b.pension,
            isCouple,
            isHomeowner: anyoneOwnsHome(factFind as unknown as FactFind),
            illnessSeparatedForPension: resolveIllnessSeparatedForSahInsight(factFind, b.financials as never, undefined, false),
        });
        const full = Math.abs(data.pensionFromAssetTest - data.pensionFromIncomeTest) < 1;
        /*
         * On the full Age Pension the contribution rates are the full pensioner ones, the lowest there are (owner,
         * 24 Oct 2026: "wouldn't the lowest contribution rate already be reached, and as such step 3 will not
         * necessarily be able to optimise their situation further"). So neither can be improved; what a plan can
         * still change is the money each year and what is left to the family.
         */
        if (full && b.pension > 0) {
            return {
                lead: <>{v.Names} {v.are} on the full Age Pension{linkedStory ? `, with ${linkedStory.enteringName} in care` : ''}.</>,
                body: <>Neither means test reduces it, and {v.their} contribution rates are already the lowest they can be: {pct(b.rates?.independence ?? 0)} and {pct(b.rates?.everydayLiving ?? 0)}, the full pensioner rates.</>,
                after: <>As a result, no plan can raise {v.their} pension or lower what {v.they} pay for each service. What the next step can still change is the money each year and what is left to the family{anyoneOwnsHome(factFind as unknown as FactFind) ? ' — for example drawing an income from the home —' : ','} and it may find that keeping things as they are is best.</>,
            };
        }
        // Short paragraphs, full width (owner, 20 Oct 2026: one long block in a narrow column "looks crammed").
        if (data.isAssetTestLimiting) {
            return {
                lead: <>For {v.names} it is the assets test, not the income test, that sets {v.couple ? v.their : v.possessive} Age Pension.</>,
                body: <>Centrelink counts <strong>{money(b.assessableAssets)}</strong> of {v.couple ? v.their : v.possessive} assets, and the assets test pays less than the income test would, so it is the one that applies: {money(b.pension)} a year. Each $1,000 less that is counted adds about {money(ANNUAL_REDUCTION_PER_1000_ASSETS)} a year to the pension, until the income test takes over.</>,
                after: <>The same assessment sets {v.their} contribution rates. So the ways most likely to help are the ones that lower what is counted — spending on the home, a funeral bond, part of a lifetime income. Ways that only lower {v.their} income would do little here. That is what the next step tests first.</>,
            };
        }
        return {
            lead: <>For {v.names} it is the income test, not the assets test, that sets {v.couple ? v.their : v.possessive} Age Pension.</>,
            body: <>Centrelink counts {money(b.assessableIncome)} a year of income, including what {v.their} savings are deemed to earn, and the income test pays less than the assets test would: {money(b.pension)} a year.</>,
            after: <>The same assessment sets {v.their} contribution rates. So the ways most likely to help are the ones that lower assessed income — for example a lifetime income, which is counted at less than its payments. Moving money into the home helps only through the income it would have been deemed to earn. That is what the next step tests first.</>,
        };
    })();

    const runway = b ? planRunwayCopy(b.projections as never, years) : null;
    /* HOW LONG THE MONEY LASTS, in aged care's shape (owner, 20 Oct 2026: "a different UI to how it's been
       done in the aged care module, including the ability to see the year by year projections"): the
       runway bar, which account carries the gap and when, the horizon note, and the year-by-year figures. */
    const sequence = useMemo(
        () => (b ? buildMoneySequence((b.projections as unknown as RunwayRow[]) || [], household, runway, linkedCare ? { todayWord: 'after the aged care plan' } : {}) : []),
        [b, household, runway, linkedCare],
    );
    const [projectionsOpen, setProjectionsOpen] = useState(false);
    const hasGap = !!b && b.projections.some((r) => Number((r as { cashFlow?: unknown }).cashFlow) < 0);
    const caps = b ? (['client', 'partner'] as const)
        .filter((k) => k === 'client' || isCouple)
        .map((k) => ({ name: String((k === 'client' ? factFind?.client?.firstName : factFind?.partner?.firstName) || (k === 'client' ? 'You' : 'Partner')), annual: b.perPerson[k] }))
        .filter((p) => p.annual > 0)
        .map((p) => ({ ...p, years: LIFETIME_CAPS.standard / p.annual })) : [];

    return (
        <div className="space-y-6" data-testid="sah-step2">
            <div>
                <h2 className="text-[22px] font-bold leading-tight tracking-tight text-brand-navy">With this support and nothing else changed</h2>
                <p className="mt-1 text-[14px] text-slate-600">{SAH_BENCH.sentence} Every plan on the next step is measured against these four numbers, which the cards call &ldquo;{SAH_BENCH.word}&rdquo;.</p>
            </div>

            {error && <p className="rounded-xl border border-slate-200 border-l-4 border-l-rose-500 bg-white px-4 py-3 text-[14px] text-slate-700" role="alert">{error}</p>}
            {!b && !error && <p className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-[14px] text-slate-600">Working out the four numbers…</p>}

            {b && (
                <div className={loading ? 'opacity-60 transition-opacity' : ''}>
                    <DoNothingStrip
                        large
                        caption={null}
                        careDaily={0}
                        careAnnual={0}
                        enteringName={v.names}
                        costCell={{ label: `${v.Their} contribution`, value: b.contribution, unit: 'a year', note: <>{servicesTotal > 0 ? `of ${money(servicesTotal)} of services` : 'no services chosen yet'} · <Why onClick={() => setPage('contribution')} /></> }}
                        pensionLabel="Age Pension"
                        pensionNoteText={<>{v.couple ? `a year, the two of ${v.them}` : 'a year'} · <Why onClick={() => setPage('pension')} /></>}
                        pension={b.pension}
                        cashflowAfterCare={b.cashflow}
                        cashflowNotes={{
                            short: <>money in, less money out and {v.their} contribution · <Why onClick={() => setPage('cashflow')} /></>,
                            over: <>money in, less money out and {v.their} contribution · <Why onClick={() => setPage('cashflow')} /></>,
                        }}
                        legacy={b.legacy}
                        horizonYears={years}
                        onWhyYears={() => setYearsOpen(true)}
                    />
                    {onYearsChange && (
                        <p className="mt-2 text-[13px] text-slate-600" data-testid="sah-step2-years">
                            <ProjectionYearsInput years={years} onChange={onYearsChange} before="Planned over" after=" — change it here; every plan on the next step is tested over the same span." ariaLabel="Years to plan over" />
                        </p>
                    )}
                </div>
            )}

            {b && (
                <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                    {linkedCare && linkedStory && (
                        <Section title={`With ${linkedStory.enteringName} moving into aged care`} testId="sah-step2-linked">
                            <SahLinkedCareSummary linkedCare={linkedCare} refundNote={false} />
                        </Section>
                    )}
                    <Section title={`${v.Their} contribution for each hour`} testId="sah-step2-rates">
                        <div className="grid grid-cols-1 overflow-hidden rounded-xl border border-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-slate-200">
                            <div className="px-4 py-3.5"><p className="text-[13px] text-slate-500">Nursing and personal care</p><p className="mt-0.5 text-[22px] font-bold text-brand-navy">Free</p><p className="mt-0.5 text-[12.5px] text-slate-500">paid by the government, within each person&apos;s budget</p></div>
                            <div className="px-4 py-3.5"><p className="text-[13px] text-slate-500">Getting out and about</p><p className="mt-0.5 text-[22px] font-bold tabular-nums text-brand-navy">{pct(b.rates?.independence ?? 0)}</p><p className="mt-0.5 text-[12.5px] text-slate-500">social support, transport</p></div>
                            <div className="px-4 py-3.5"><p className="text-[13px] text-slate-500">Help around the house</p><p className="mt-0.5 text-[22px] font-bold tabular-nums text-brand-navy">{pct(b.rates?.everydayLiving ?? 0)}</p><p className="mt-0.5 text-[12.5px] text-slate-500">cleaning, meals</p></div>
                        </div>
                        <p className="mt-2 text-[13px] text-slate-500">
                            Set by Services Australia&apos;s means assessment. Have {v.their} letter, with rates that differ from these? <Why onClick={onEnterLetterRates}>Enter the rates from the letter</Why>
                        </p>
                    </Section>

                    {pensionStory && (
                        <Section title={`Why ${v.their} contribution is what it is`} testId="sah-step2-why">
                            <p className="text-[15px] leading-relaxed text-slate-700"><strong className="text-slate-900">{pensionStory.lead}</strong> {pensionStory.body}</p>
                            {'after' in pensionStory && pensionStory.after && <p className="mt-2 text-[15px] leading-relaxed text-slate-700">{pensionStory.after}</p>}
                        </Section>
                    )}

                    <Section title="How long the money lasts" testId="sah-step2-runway">
                        {runway ? (
                            <FundingRunway
                                headline={<span className="text-[18px] font-bold leading-tight tracking-tight text-slate-900">{runway.headline}</span>}
                                coveredPct={runway.coveredPct}
                                leftLabel={runway.leftLabel}
                                rightLabel={runway.rightLabel}
                                depleted={runway.depleted}
                            />
                        ) : (
                            <p className="text-[15px] text-slate-700">{b.cashflow < 0 ? `Short by ${money(b.cashflow)} a year.` : `${money(b.cashflow)} a year to spare.`}</p>
                        )}
                        {/* Which account carries the gap — only when there is one (it read "From year 1 the gap
                            comes out of…" under "no savings are drawn down", owner's screenshot 20 Oct 2026). */}
                        {hasGap && sequence.length > 0 && (
                            <ol className="mt-4 grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-3 gap-y-2" data-testid="sah-step2-sequence">
                                {sequence.map((e, i) => (
                                    <React.Fragment key={i}>
                                        <li className="list-none"><span className="inline-flex w-full justify-center rounded-md bg-slate-100 px-2 py-1 text-[12px] font-bold uppercase tracking-wide text-slate-600">{e.from}</span></li>
                                        <li className="list-none pt-0.5 text-[14px] leading-relaxed text-slate-600">{e.text}</li>
                                    </React.Fragment>
                                ))}
                            </ol>
                        )}
                        {runway && <HorizonNote runway={runway} className="mt-3" />}
                        {linkedStory && linkedStory.deposit >= 1 && <p className="mt-3 text-[14px] leading-relaxed text-slate-600" data-testid="sah-step2-rad-refund">{depositRefundSentence(linkedStory.enteringName)}</p>}
                        {caps.length > 0 && (
                            <p className="mt-3 text-[14px] leading-relaxed text-slate-600">
                                {caps.map((c, i) => (
                                    <React.Fragment key={c.name}>
                                        {i > 0 ? ' ' : ''}{c.name}&apos;s contributions would reach the {money(LIFETIME_CAPS.standard)} lifetime limit {c.years > 40 ? 'only after more than 40 years' : `in about ${Math.round(c.years)} ${Math.round(c.years) === 1 ? 'year' : 'years'}`} at this rate.
                                    </React.Fragment>
                                ))}
                            </p>
                        )}
                        <button type="button" onClick={() => setProjectionsOpen(true)} className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-semibold text-blue-700 hover:border-blue-300 hover:bg-blue-50" data-testid="sah-step2-year-by-year">
                            <Eye size={15} /> See the projections, year by year
                        </button>
                        {showSeparated && separated.figures?.rates && (
                            <div className="mt-3 rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] text-slate-700" data-testid="sah-step2-separated">
                                If Centrelink assesses {v.them} as an illness-separated couple — for example when {String((entering === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || `one of ${v.them}`)} moves into care — {v.their} rates would be {pct(separated.figures.rates.independence)} and {pct(separated.figures.rates.everydayLiving)}.{' '}
                                <Why onClick={() => setSeparatedOpen(true)}>What does that mean?</Why>
                            </div>
                        )}
                    </Section>
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
                <button type="button" onClick={onBack} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy">‹ Change the services</button>
                <Button type="button" variant="brand" size="lg" onClick={onNext} data-testid="sah-step2-next">See ways to improve {v.their} position →</Button>
            </div>

            {page && fig && (
                <SahFigurePopover
                    planTitle="The support alone — the same services, nothing else changed"
                    plan={fig}
                    baseline={null}
                    initialPage={page}
                    possessive={v.their}
                    they={v.they}
                    them={v.them}
                    pensionWhy={pensionStory ? <>{pensionStory.lead}</> : 'The Age Pension is worked out under both the assets test and the income test; the lower result is paid.'}
                    onClose={() => setPage(null)}
                />
            )}
            {projectionsOpen && b && (
                <SupportAtHomeSuggestionEngineDeepDive
                    strategy={{ name: 'With these services, and nothing else changed', projections: b.projections } as unknown as SupportAtHomeSuggestionEngineDeepDiveProps['strategy']}
                    factFind={household}
                    onClose={() => setProjectionsOpen(false)}
                />
            )}
            {yearsOpen && (
                <DialogShell titleId="sah-years-heading" title={`Why about ${years} years?`} intro="What is left to the family is projected over the years we plan for." onClose={() => setYearsOpen(false)}
                    footer={<Button type="button" variant="primary" onClick={() => setYearsOpen(false)}>Got it</Button>}>
                    <p className="text-[14px] leading-relaxed text-slate-700">
                        We plan Support at home over {years} years: long enough for a plan&apos;s cost to show against what it saves, and the same span as the other living options. Change it under the four numbers; every plan is then tested over the new span.
                    </p>
                </DialogShell>
            )}
            {separatedOpen && (
                <DialogShell titleId="sah-separated-heading" title="An illness-separated couple" intro="How Centrelink treats a couple when one of them cannot live at home." onClose={() => setSeparatedOpen(false)}
                    footer={<Button type="button" variant="primary" onClick={() => setSeparatedOpen(false)}>Got it</Button>}>
                    <p className="text-[14px] leading-relaxed text-slate-700">
                        When one of a couple moves into care, Centrelink usually treats them as illness-separated: each is paid the single rate of Age Pension, assessed against the couple&apos;s limits. The same assessment sets the Support at home contribution rates, so they change too. If {String((entering === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'one of them')} is moving, answer &quot;Yes&quot; to the question on step 1 and every figure is priced that way.
                    </p>
                </DialogShell>
            )}
        </div>
    );
};

export default SahStep2;
